import { StateSchema, type StateType } from "@remi/types/state";

import { titleCase } from "./name";

export const states: Record<string, string> = {
  AZ: "Arizona",
  AL: "Alabama",
  AK: "Alaska",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DC: "District of Columbia",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  AS: "American Samoa",
  GU: "Guam",
  MP: "Northern Mariana Islands",
  PR: "Puerto Rico",
  VI: "U.S. Virgin Islands",
  UM: "U.S. Minor Outlying Islands",
};

const codes: Record<string, string> = Object.fromEntries(
  Object.entries(states).map(([k, v]) => [v.toLowerCase().replace(/[^a-z]/g, ""), k]),
);

/**
 * Convert a state abbreviation to the full name
 * @example stateCodeToName("AZ") // "Arizona"
 * @param code the State Code
 * @returns the State Name
 */
export function stateCodeToName(code: string | null | undefined) {
  if (!code) {
    return "";
  }
  const standardCode = code.toUpperCase().replace(/[^A-Z]/g, "");
  return states[standardCode] ?? titleCase(code);
}

/**
 * Convert a state name to the abbreviation
 * @example stateNameToCode("Arizona") // "AZ"
 * @param name the State Name
 * @returns the State Code
 */
export function stateNameToCode(name: string | null | undefined) {
  if (!name) {
    return "";
  }
  const standardName = name.toLowerCase().replace(/[^a-z]/g, "");
  return codes[standardName] ?? name.toUpperCase();
}

/**
 * Convert a state name to the abbreviation in a type-safe way
 * @example stateNameToCode("Arizona") // "AZ"
 * @param name the State Name
 * @returns the State Code or "UNKNOWN" if not found
 */
export function stateNameToEnum(name: string | null | undefined): StateType {
  if (!name) {
    return "UNKNOWN";
  }
  const checkIfEnum = StateSchema.safeParse(name as unknown);
  if (checkIfEnum.success) {
    return checkIfEnum.data;
  }
  const standardName = name.toLowerCase().replace(/[^a-z]/g, "");
  const code = codes[standardName];
  const parsedRes = StateSchema.safeParse(code as unknown);
  return parsedRes.success ? parsedRes.data : "UNKNOWN";
}
